// Diario de actividad del TPV — middleware de Redux.
//
// Traduce las acciones del carrito que dispara el camarero en eventos del
// diario comparando el carrito antes/después (así se registran también los
// productos que se añaden y se quitan sin llegar nunca a comandarse, que no
// dejan rastro en la base de datos). Solo mira una lista cerrada de
// acciones de usuario: las sincronizaciones con el servidor, restauraciones
// de borrador y el vaciado tras comandar/cobrar NO generan eventos.

import { diffCart } from "./journalCore";
import journal, { setStateGetter } from "./journal";
import { installUiErrorCapture } from "./uiErrorCapture";

// Acciones de usuario sobre el carrito → diff antes/después.
export const CART_ACTIONS = new Set([
  "product/addCart",
  "product/addEditCart",
  "product/increaseQuantity",
  "product/decreaseQuantity",
  "product/deleteCart",
  "product/replaceMenuLines",
]);

const cartOf = (state) => state?.product?.checkout?.cart;

/** Eventos del diario para una acción, dado el estado antes/después (puro). */
export function journalEventsFor(action, before, after) {
  const type = action?.type;
  if (CART_ACTIONS.has(type)) {
    return diffCart(cartOf(before), cartOf(after));
  }
  if (type === "product/applyDiscount") {
    return [{ type: "discount.apply", data: { discount: Number(action.payload) || 0 } }];
  }
  if (type === "product/moveProductsToComensal") {
    const p = action.payload || {};
    return [{
      type: "comensal.assign",
      data: {
        to: p.newComensal ?? null,
        lines: Array.isArray(p.productIds) ? p.productIds.length : 0,
        ...(p.units != null ? { units: p.units } : {}),
      },
    }];
  }
  return [];
}

export const journalMiddleware = (storeAPI) => {
  setStateGetter(storeAPI.getState);
  // Excepciones de interfaz → ui.error con stack (solo pantallas TPV).
  installUiErrorCapture((type, data) => journal.track(type, data));
  return (next) => (action) => {
    const type = action?.type;
    if (typeof type !== "string" || !type.startsWith("product/")) return next(action);
    const before = storeAPI.getState();
    const result = next(action);
    try {
      const events = journalEventsFor(action, before, storeAPI.getState());
      for (const ev of events) journal.track(ev.type, ev.data);
    } catch { /* el diario nunca rompe la operativa */ }
    return result;
  };
};

export default journalMiddleware;
